import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { studentsListOnly } from "../../api/Student";
import { busStopsList, studentBusEnroll } from "../../api/Transport";
import { SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeRegistration = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeStopName = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const todayStr = () => new Date().toISOString().split("T")[0];

const parseExcelDate = (value) => {
  if (!value) return "";

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return "";
    const mm = String(parsed.m).padStart(2, "0");
    const dd = String(parsed.d).padStart(2, "0");
    return `${parsed.y}-${mm}-${dd}`;
  }

  const str = String(value).trim();
  if (!str) return "";

  const parsedDate = new Date(str);
  if (!isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split("T")[0];
  }

  return "";
};

const PICKER_RESULT_LIMIT = 50;

export default function BusStudentImport() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");

  const [students, setStudents] = useState([]);
  const [stops, setStops] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [paymentType, setPaymentType] = useState("Full Year");
  const [defaultJoiningDate, setDefaultJoiningDate] = useState(todayStr());

  const [rows, setRows] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [updateStatus, setUpdateStatus] = useState({});

  const [pickerRowId, setPickerRowId] = useState(null);
  const [pickerType, setPickerType] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");

  const fileInputRef = useRef(null);

  useEffect(() => {
    const storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(storedSessions);

    const activeSession =
      storedSessions.find((s) => String(s.status) === "1") || storedSessions[0];

    if (activeSession?.id) {
      setSessionId(String(activeSession.id));
      fetchMeta(activeSession.id);
    }
  }, []);

  const fetchMeta = async (sid) => {
    if (!sid) return;

    setLoadingMeta(true);

    try {
      const [studentsRes, stopsRes] = await Promise.all([
        studentsListOnly(),
        busStopsList(`limit=all&session_id=${sid}`),
      ]);

      setStudents(studentsRes?.data?.status ? studentsRes.data.data || [] : []);
      setStops(stopsRes?.data?.status ? stopsRes.data.data || [] : []);

      if (!studentsRes?.data?.status) {
        SweetAlertShow(
          "info",
          "Warning",
          studentsRes?.data?.message || "Unable to load students.",
        );
      }

      if (!stopsRes?.data?.status) {
        SweetAlertShow(
          "info",
          "Warning",
          stopsRes?.data?.message || "Unable to load bus stops.",
        );
      }
    } catch (error) {
      setStudents([]);
      setStops([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load data.",
      );
    } finally {
      setLoadingMeta(false);
    }
  };

  const handleSessionChange = (value) => {
    setSessionId(value);
    setStudents([]);
    setStops([]);
    setRows([]);
    setUpdateStatus({});
    closePicker();

    if (value) fetchMeta(value);
  };

  const studentByReg = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      const key = normalizeRegistration(student.registration_no);
      if (key) map.set(key, student);
    });
    return map;
  }, [students]);

  const stopByName = useMemo(() => {
    const map = new Map();
    stops.forEach((stop) => {
      const key = normalizeStopName(stop.stop_name);
      if (key && !map.has(key)) map.set(key, stop);
    });
    return map;
  }, [stops]);

  const openPicker = (rowId, type) => {
    setPickerRowId(rowId);
    setPickerType(type);
    setPickerSearch("");
  };

  const closePicker = () => {
    setPickerRowId(null);
    setPickerType(null);
    setPickerSearch("");
  };

  const filteredPickerStudents = useMemo(() => {
    if (pickerType !== "student") return [];
    const term = normalize(pickerSearch);
    const regTerm = normalizeRegistration(pickerSearch);

    const list = students.filter((s) => {
      if (!term) return true;
      return (
        normalize(s.name).includes(term) ||
        normalizeRegistration(s.registration_no).includes(regTerm) ||
        normalize(s.phone).includes(term)
      );
    });

    return list.slice(0, PICKER_RESULT_LIMIT);
  }, [students, pickerSearch, pickerType]);

  const filteredPickerStops = useMemo(() => {
    if (pickerType !== "stop") return [];
    const term = normalize(pickerSearch);

    const list = [...stops]
      .sort((a, b) =>
        (a?.stop_name || "").localeCompare(b?.stop_name || "", undefined, {
          sensitivity: "base",
        }),
      )
      .filter((s) => {
        if (!term) return true;
        return (
          normalize(s.stop_name).includes(term) ||
          normalize(s?.bus?.bus_number).includes(term) ||
          normalize(s?.bus?.driver_name).includes(term)
        );
      });

    return list.slice(0, PICKER_RESULT_LIMIT);
  }, [stops, pickerSearch, pickerType]);

  const selectStudentForRow = (rowId, student) => {
    updateRow(rowId, {
      student,
      registration_raw: student.registration_no || student.name,
    });
    closePicker();
  };

  const selectStopForRow = (rowId, stop) => {
    updateRow(rowId, {
      stop,
      stop_raw: stop.stop_name,
      final_fee: num(stop.stop_fee),
      discount_remark: "",
    });
    closePicker();
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!sessionId) {
      event.target.value = "";
      return SweetAlertShow(
        "warning",
        "Select Session",
        "Please select a session first.",
      );
    }

    if (!students.length || !stops.length) {
      event.target.value = "";
      return SweetAlertShow(
        "warning",
        "Data Not Ready",
        "Students or bus stops are not loaded yet for this session.",
      );
    }

    setExcelLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const excelRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (!excelRows.length) throw new Error("Excel file is empty.");

      const firstRow = excelRows[0];
      const keyOf = (candidates) =>
        Object.keys(firstRow).find((key) => {
          const k = normalize(key).replace(/[\s_-]/g, "");
          return candidates.includes(k);
        });

      const registrationKey = keyOf([
        "registrationno",
        "registrationnumber",
        "registration",
        "regno",
        "admissionno",
      ]);

      const stopKey = keyOf([
        "stopname",
        "stop",
        "busstop",
        "route",
        "routename",
      ]);
      const dateKey = keyOf(["joiningdate", "date", "doj"]);
      const feeKey = keyOf(["finalfee", "fee", "amount"]);

      if (!registrationKey) {
        throw new Error(
          "Registration number column not found. Use 'registration_no'.",
        );
      }

      if (!stopKey) {
        throw new Error("Stop name column not found. Use 'stop_name'.");
      }

      const built = excelRows.map((row, index) => {
        const registrationRaw = row[registrationKey];
        const stopRaw = row[stopKey];

        const student =
          studentByReg.get(normalizeRegistration(registrationRaw)) || null;
        const stop = stopByName.get(normalizeStopName(stopRaw)) || null;

        const rowDate = dateKey ? parseExcelDate(row[dateKey]) : "";
        const overrideFee = feeKey ? row[feeKey] : "";

        const stopFee = stop ? num(stop.stop_fee) : 0;
        const finalFee =
          overrideFee !== "" && !isNaN(Number(overrideFee))
            ? Math.min(num(overrideFee), stopFee || num(overrideFee))
            : stopFee;

        return {
          rowId: `row-${index}-${Date.now()}`,
          registration_raw: registrationRaw,
          stop_raw: stopRaw,
          student,
          stop,
          final_fee: finalFee,
          joining_date: rowDate || defaultJoiningDate || todayStr(),
          discount_remark: "",
        };
      });

      setRows(built);
      setUpdateStatus({});
      closePicker();

      const matched = built.filter((r) => r.student && r.stop).length;
      const unmatched = built.length - matched;

      SweetAlertShow(
        matched ? "success" : "warning",
        "Excel Processed",
        `${matched} matched, ${unmatched} not matched out of ${built.length} rows. Unmatched rows can be fixed manually below.`,
      );
    } catch (error) {
      SweetAlertShow(
        "error",
        "Excel Error",
        error?.message || "Unable to process file.",
      );
    } finally {
      setExcelLoading(false);
      event.target.value = "";
    }
  };

  const updateRow = (rowId, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)),
    );
  };

  const removeRow = (rowId) => {
    setRows((prev) => prev.filter((row) => row.rowId !== rowId));
    setUpdateStatus((prev) => {
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
    if (pickerRowId === rowId) closePicker();
  };

  const validateRow = (row) => {
    const errors = [];

    if (!row.student) errors.push("Student not selected.");
    if (!row.stop) errors.push("Bus stop not selected.");

    if (row.student && row.stop) {
      const stopFee = num(row.stop.stop_fee);
      const finalFee = num(row.final_fee);

      if (finalFee < 0) errors.push("Fee cannot be negative.");
      if (finalFee > stopFee) errors.push("Fee cannot exceed stop fee.");
      if (finalFee < stopFee && !row.discount_remark?.trim()) {
        errors.push("Discount remark required when fee is reduced.");
      }
    }

    return errors;
  };

  const buildFormData = (row) => {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("student_id", row.student.id);
    formData.append("bus_id", row.stop.bus_id);
    formData.append("bus_stop_id", row.stop.id);
    formData.append("final_fee", num(row.final_fee));
    formData.append("payment_type", paymentType);
    formData.append("joining_date", row.joining_date || todayStr());
    if (row.discount_remark?.trim()) {
      formData.append("discount_remark", row.discount_remark.trim());
    }
    return formData;
  };

  const applyRow = async (rowId) => {
    const row = rows.find((r) => r.rowId === rowId);
    if (!row) return;

    const errors = validateRow(row);
    if (errors.length) {
      return SweetAlertShow("error", "Validation Error", errors.join("\n"));
    }

    setApplying(true);

    try {
      const response = await studentBusEnroll(buildFormData(row));

      if (response?.data?.status) {
        setUpdateStatus((prev) => ({
          ...prev,
          [rowId]: {
            status: "success",
            message: response?.data?.message || "Enrolled",
          },
        }));
      } else {
        throw new Error(
          Array.isArray(response?.data?.message)
            ? response.data.message.join(", ")
            : response?.data?.message || "Enrollment failed",
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Enrollment failed";

      setUpdateStatus((prev) => ({
        ...prev,
        [rowId]: {
          status: "error",
          message: Array.isArray(message)
            ? message.join(", ")
            : String(message),
        },
      }));
    } finally {
      setApplying(false);
    }
  };


  const applyAll = async () => {
    const pending = rows.filter(
      (row) => updateStatus[row.rowId]?.status !== "success",
    );

    if (!pending.length) {
      return SweetAlertShow(
        "info",
        "Nothing To Apply",
        "No pending rows to enroll.",
      );
    }

    const invalid = pending
      .map((row) => ({ row, errors: validateRow(row) }))
      .filter((x) => x.errors.length);

    if (invalid.length) {
      const first = invalid[0];
      return SweetAlertShow(
        "error",
        "Validation Error",
        `${first.row.student?.name || first.row.registration_raw || "Row"}: ${first.errors.join(", ")}`,
      );
    }

    setApplying(true);

    const status = { ...updateStatus };

    try {
      const chunkSize = 10;

      for (let i = 0; i < pending.length; i += chunkSize) {
        const chunk = pending.slice(i, i + chunkSize);

        await Promise.allSettled(
          chunk.map(async (row) => {
            try {
              const response = await studentBusEnroll(buildFormData(row));

              status[row.rowId] = response?.data?.status
                ? {
                    status: "success",
                    message: response?.data?.message || "Enrolled",
                  }
                : {
                    status: "error",
                    message: Array.isArray(response?.data?.message)
                      ? response.data.message.join(", ")
                      : response?.data?.message || "Enrollment failed",
                  };
            } catch (error) {
              status[row.rowId] = {
                status: "error",
                message:
                  error?.response?.data?.message ||
                  error?.message ||
                  "Enrollment failed",
              };
            }
          }),
        );

        setUpdateStatus({ ...status });
      }

      const success = Object.values(status).filter(
        (x) => x.status === "success",
      ).length;
      const failed = Object.values(status).filter(
        (x) => x.status === "error",
      ).length;

      SweetAlertShow(
        failed === 0 ? "success" : "warning",
        "Import Completed",
        `Enrolled: ${success}, Failed: ${failed}`,
      );
    } finally {
      setApplying(false);
    }
  };

  const summary = useMemo(() => {
    const matched = rows.filter((r) => r.student && r.stop).length;
    const unmatched = rows.length - matched;
    const enrolled = rows.filter(
      (r) => updateStatus[r.rowId]?.status === "success",
    ).length;
    const failed = rows.filter(
      (r) => updateStatus[r.rowId]?.status === "error",
    ).length;
    const pending = rows.length - enrolled;

    return {
      total: rows.length,
      matched,
      unmatched,
      enrolled,
      failed,
      pending,
    };
  }, [rows, updateStatus]);

  const renderStudentCell = (row) => {
    const applied = updateStatus[row.rowId]?.status === "success";
    const isPickerOpenHere =
      pickerRowId === row.rowId && pickerType === "student";

    if (row.student && !isPickerOpenHere) {
      return (
        <div>
          <div className="d-flex justify-content-between align-items-start gap-1">
            <div>
              <div className="fw-semibold">
                <i className="ri-user-3-line me-1 text-primary" />
                {row.student.name}
              </div>
              <small className="text-muted">
                Reg: {row.student.registration_no || "-"}
              </small>
            </div>
            {!applied && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-secondary"
                title="Change student"
                onClick={() => openPicker(row.rowId, "student")}
              >
                <i className="ri-pencil-line" />
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className="position-relative"
        style={{ zIndex: isPickerOpenHere ? 30 : 1 }}
      >
        <div className="input-group input-group-sm">
          <span className="input-group-text">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search student..."
            value={isPickerOpenHere ? pickerSearch : ""}
            onFocus={() => openPicker(row.rowId, "student")}
            onChange={(e) => {
              if (!isPickerOpenHere) openPicker(row.rowId, "student");
              setPickerSearch(e.target.value);
            }}
            onBlur={() => setTimeout(() => closePicker(), 150)}
            autoComplete="off"
          />
        </div>

        {!row.student && (
          <small className="text-danger d-block mt-1">
            <i className="ri-error-warning-line me-1" />
            Not found: "{row.registration_raw}"
          </small>
        )}

        {isPickerOpenHere && (
          <div
            className="position-absolute bg-white border rounded-2 shadow-sm w-100 mt-1"
            style={{ maxHeight: 220, overflowY: "auto", zIndex: 50 }}
          >
            {filteredPickerStudents.length ? (
              filteredPickerStudents.map((s) => (
                <div
                  key={s.id}
                  className="px-2 py-1 border-bottom"
                  style={{ cursor: "pointer", fontSize: 12.5 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectStudentForRow(row.rowId, s)}
                >
                  <div className="fw-semibold">{s.name}</div>
                  <div className="text-muted">
                    {s.registration_no || "-"} · {s.phone || "-"}
                  </div>
                </div>
              ))
            ) : (
              <div
                className="text-center text-muted py-2"
                style={{ fontSize: 12.5 }}
              >
                <i className="ri-user-search-line me-1" />
                No student found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStopCell = (row) => {
    const applied = updateStatus[row.rowId]?.status === "success";
    const isPickerOpenHere = pickerRowId === row.rowId && pickerType === "stop";

    if (row.stop && !isPickerOpenHere) {
      return (
        <div>
          <div className="d-flex justify-content-between align-items-start gap-1">
            <div>
              <div className="fw-semibold">
                <i className="ri-map-pin-2-line me-1 text-primary" />
                {row.stop.stop_name}
              </div>
              <small className="text-muted">
                <i className="ri-bus-2-line me-1" />
                {row.stop.bus?.bus_number || "-"}
              </small>
            </div>
            {!applied && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-secondary"
                title="Change stop"
                onClick={() => openPicker(row.rowId, "stop")}
              >
                <i className="ri-pencil-line" />
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className="position-relative"
        style={{ zIndex: isPickerOpenHere ? 30 : 1 }}
      >
        <div className="input-group input-group-sm">
          <span className="input-group-text">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search stop / bus..."
            value={isPickerOpenHere ? pickerSearch : ""}
            onFocus={() => openPicker(row.rowId, "stop")}
            onChange={(e) => {
              if (!isPickerOpenHere) openPicker(row.rowId, "stop");
              setPickerSearch(e.target.value);
            }}
            onBlur={() => setTimeout(() => closePicker(), 150)}
            autoComplete="off"
          />
        </div>

        {!row.stop && (
          <small className="text-danger d-block mt-1">
            <i className="ri-error-warning-line me-1" />
            Not found: "{row.stop_raw}"
          </small>
        )}

        {isPickerOpenHere && (
          <div
            className="position-absolute bg-white border rounded-2 shadow-sm w-100 mt-1"
            style={{ maxHeight: 220, overflowY: "auto", zIndex: 50 }}
          >
            {filteredPickerStops.length ? (
              filteredPickerStops.map((s) => (
                <div
                  key={s.id}
                  className="px-2 py-1 border-bottom"
                  style={{ cursor: "pointer", fontSize: 12.5 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectStopForRow(row.rowId, s)}
                >
                  <div className="fw-semibold">
                    <i className="ri-map-pin-2-line me-1 text-primary" />
                    {s.stop_name}
                  </div>
                  <div className="text-muted">
                    <i className="ri-bus-2-line me-1" />
                    {s.bus?.bus_number || "-"}
                    {" · "}
                    {s.bus?.driver_name || "Driver not assigned"}
                  </div>
                  <div className="text-success">₹{money(s.stop_fee)}</div>
                </div>
              ))
            ) : (
              <div
                className="text-center text-muted py-2"
                style={{ fontSize: 12.5 }}
              >
                <i className="ri-search-line me-1" />
                No stop or bus found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {applying && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: "rgba(0,0,0,.25)", zIndex: 9999 }}
        >
          <div className="spinner-border text-primary" />
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* HEADER + SETTINGS CARD */}
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "14px",
                              background:
                                "linear-gradient(135deg,#185fa5,#4f8cff)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 22,
                              boxShadow: "0 4px 12px rgba(24,95,165,0.25)",
                            }}
                          >
                            <i className="ri-file-excel-2-line" />
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">
                              Bulk Bus Student Import
                            </h5>
                            <small className="text-muted">
                              Enroll many students to bus stops in one go via
                              Excel
                            </small>
                          </div>
                        </div>

                        <div className="row g-3">
                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={sessionId}
                                onChange={(e) =>
                                  handleSessionChange(e.target.value)
                                }
                              >
                                <option value="">Select Session</option>
                                {sessions.map((session) => (
                                  <option key={session.id} value={session.id}>
                                    {session.session}
                                    {String(session.status) === "1"
                                      ? " (Active)"
                                      : ""}
                                  </option>
                                ))}
                              </select>
                              <label>
                                <i className="ri-calendar-line me-1" />
                                Session
                              </label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={paymentType}
                                onChange={(e) => setPaymentType(e.target.value)}
                                disabled={!sessionId}
                              >
                                <option value="Full Year">Full Year</option>
                                <option value="Half Year">Half Year</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Monthly">Monthly</option>
                              </select>
                              <label>
                                <i className="ri-calendar-schedule-line me-1" />
                                Payment Type (applies to all)
                              </label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                className="form-control"
                                value={defaultJoiningDate}
                                onChange={(e) =>
                                  setDefaultJoiningDate(e.target.value)
                                }
                                disabled={!sessionId}
                              />
                              <label>
                                <i className="ri-calendar-event-line me-1" />
                                Default Joining Date
                              </label>
                            </div>
                            <small className="text-muted d-block mt-1">
                              Used when Excel has no date for a row
                            </small>
                          </div>

                          <div className="col-md-3">
                            <div className="card bg-light border mb-0 h-100">
                              <div className="card-body py-2">
                                <small className="text-muted">
                                  Rows Loaded
                                </small>
                                <h4 className="mb-0">{summary.total}</h4>
                                {summary.total > 0 && (
                                  <small className="text-muted">
                                    {summary.matched} matched ·{" "}
                                    {summary.unmatched} unmatched
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* UPLOAD ROW */}
                        {!!sessionId && (
                          <>
                            <hr />
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={
                                  loadingMeta || excelLoading || applying
                                }
                              >
                                {excelLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Processing Excel...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-upload-2-line me-1" />
                                    Upload Excel
                                  </>
                                )}
                              </button>

                              <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="d-none"
                                onChange={handleExcelUpload}
                              />

                              <button
                                className="btn btn-success"
                                onClick={applyAll}
                                disabled={
                                  !rows.length ||
                                  applying ||
                                  summary.pending === 0
                                }
                              >
                                {applying ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Applying...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-checkbox-circle-line me-1" />
                                    Apply All
                                    {summary.pending > 0 &&
                                      ` (${summary.pending})`}
                                  </>
                                )}
                              </button>

                              <small className="text-muted">
                                Excel must contain{" "}
                                <strong>registration_no</strong> and{" "}
                                <strong>stop_name</strong> columns. Optional:{" "}
                                <strong>joining_date</strong>. Unmatched rows
                                can be fixed manually below with search.
                              </small>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* SUMMARY STRIP */}
                    {rows.length > 0 && (
                      <div className="row g-2 mb-3">
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Total</small>
                              <h6 className="mb-0">{summary.total}</h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Matched</small>
                              <h6 className="mb-0 text-primary">
                                {summary.matched}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Unmatched</small>
                              <h6 className="mb-0 text-danger">
                                {summary.unmatched}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Enrolled</small>
                              <h6 className="mb-0 text-success">
                                {summary.enrolled}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Failed</small>
                              <h6 className="mb-0 text-warning">
                                {summary.failed}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MAIN TABLE / EMPTY STATES */}
                    <div className="card border-0 shadow-sm">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Students</h5>
                        {rows.length > 0 && (
                          <span className="badge bg-primary">
                            {rows.length} rows
                          </span>
                        )}
                      </div>

                      <div className="card-body p-0">
                        {!sessionId ? (
                          <div className="text-center py-5">
                            <i
                              className="ri-calendar-line text-muted"
                              style={{ fontSize: 50 }}
                            />
                            <h5 className="mt-3">Select a Session</h5>
                            <p className="text-muted mb-0">
                              Choose a session above to load students and bus
                              stops.
                            </p>
                          </div>
                        ) : loadingMeta ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary mb-3" />
                            <h6 className="text-muted mb-0">
                              Loading students &amp; bus stops...
                            </h6>
                          </div>
                        ) : !rows.length ? (
                          <div className="text-center py-5">
                            <i
                              className="ri-file-excel-2-line text-muted"
                              style={{ fontSize: 50 }}
                            />
                            <h5 className="mt-3">Upload an Excel File</h5>
                            <p className="text-muted mb-0">
                              Import registration numbers and stop names to
                              bulk-enroll students.
                            </p>
                          </div>
                        ) : (
                          <div
                            className="table-responsive"
                            style={{
                              maxHeight: "calc(100vh - 320px)",
                              overflowY: "auto",
                            }}
                          >
                            <table className="table table-bordered table-hover align-middle mb-0">
                              <thead
                                className="table-dark"
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 10,
                                }}
                              >
                                <tr>
                                  <th>#</th>
                                  <th style={{ minWidth: 240 }}>Student</th>
                                  <th style={{ minWidth: 240 }}>Bus Stop</th>
                                  <th style={{ minWidth: 140 }}>Fee</th>
                                  <th style={{ minWidth: 160 }}>
                                    Joining Date
                                  </th>
                                  <th style={{ minWidth: 160 }}>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {rows.map((row, index) => {
                                  const status = updateStatus[row.rowId];
                                  const stopFee = row.stop
                                    ? num(row.stop.stop_fee)
                                    : 0;
                                  const discounted =
                                    row.stop && num(row.final_fee) < stopFee;
                                  const applied = status?.status === "success";

                                  return (
                                    <tr
                                      key={row.rowId}
                                      className={
                                        !row.student || !row.stop
                                          ? "table-danger"
                                          : applied
                                            ? "table-success"
                                            : ""
                                      }
                                    >
                                      <td>{index + 1}</td>

                                      <td>{renderStudentCell(row)}</td>

                                      <td>{renderStopCell(row)}</td>

                                      <td>
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text">
                                            ₹
                                          </span>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={row.final_fee}
                                            disabled={!row.stop || applied}
                                            onChange={(e) =>
                                              updateRow(row.rowId, {
                                                final_fee: e.target.value,
                                              })
                                            }
                                          />
                                        </div>
                                        {row.stop && (
                                          <small className="text-muted d-block mt-1">
                                            Stop fee: ₹{money(stopFee)}
                                          </small>
                                        )}
                                        {discounted && (
                                          <input
                                            type="text"
                                            className="form-control form-control-sm mt-1"
                                            placeholder="Discount remark"
                                            value={row.discount_remark}
                                            disabled={applied}
                                            onChange={(e) =>
                                              updateRow(row.rowId, {
                                                discount_remark: e.target.value,
                                              })
                                            }
                                          />
                                        )}
                                      </td>

                                      <td>
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={row.joining_date}
                                          disabled={applied}
                                          onChange={(e) =>
                                            updateRow(row.rowId, {
                                              joining_date: e.target.value,
                                            })
                                          }
                                        />
                                      </td>

                                      <td>
                                        <div className="d-flex gap-1">
                                          <button
                                            className={`btn btn-sm ${
                                              applied
                                                ? "btn-outline-secondary"
                                                : "btn-primary"
                                            }`}
                                            onClick={() => applyRow(row.rowId)}
                                            disabled={
                                              applying ||
                                              applied ||
                                              !row.student ||
                                              !row.stop
                                            }
                                          >
                                            <i
                                              className={`me-1 ${
                                                applied
                                                  ? "ri-checkbox-circle-fill"
                                                  : "ri-send-plane-line"
                                              }`}
                                            />
                                            {applied ? "Done" : "Apply"}
                                          </button>

                                          {!applied && (
                                            <button
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() =>
                                                removeRow(row.rowId)
                                              }
                                              title="Remove row"
                                            >
                                              <i className="ri-close-line" />
                                            </button>
                                          )}
                                        </div>

                                        {status && (
                                          <div className="mt-1">
                                            {status.status === "success" ? (
                                              <span className="badge bg-success">
                                                Enrolled
                                              </span>
                                            ) : (
                                              <>
                                                <span className="badge bg-danger">
                                                  Failed
                                                </span>
                                                <div
                                                  className="text-danger mt-1"
                                                  style={{ fontSize: 11 }}
                                                >
                                                  {status.message}
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
